import { OrderBaseView } from "../../../order/base";
import { Template } from "../../../../../../core/template/index";
import { FortumApp } from "../../../../../fortumApp";
import { View } from "../../../../../../core/view/index";
import { OrderLike } from "../../../../../db/models/decontamination/order";
import { OrderItemLike } from "../../../../../db/models/decontamination/orderItem";
import { OrderDetailViewData } from "../../detail/index";
import MessageModal from "../../../../../../core/modals/message/index";
import { ModalColor } from "../../../../../../core/modal/index";
import ConfirmModal from "../../../../../../core/modals/confirm/index";
import { ClientAction } from "../../../../../synchronizer/index";
import { OrderImage } from "../../../../../db/models/decontamination";

export default class OrderPhotographingTabView extends View
{
    public template = Template.lazy('/app/views/decontamination/order/tab/photographing/index.html');
    public app: FortumApp;
    public data: OrderPhotographingTabViewData;
    private imageField: HTMLInputElement;
    private imageFilenameField: HTMLInputElement
    private clearImageButton: HTMLButtonElement;
    private imagePreview: HTMLImageElement;
    private saveImageButton: HTMLButtonElement;
    private addImageButton: HTMLLabelElement;

    constructor(data?: OrderPhotographingTabViewData) 
    {
        super(data);
    }

    getImageUrl(image: OrderImage) {
        if (image.file.startsWith('data:')) {
            return image.file;
        } else {
            return `${this.app.settings.trackingcloudUrl}${image.file}`;
        }
    }

    public beforeRender(): PromiseLike<void>
    {
        return super.beforeRender();
    }

    public afterRender(): PromiseLike<void>
    {        
        return super.afterRender().then(() =>
        {
            this.imageField.addEventListener('change', (e: Event) =>
            {
                this.onImageFieldChange(e);
            });

            this.onClearImageButtonClick(null, null);
        });
    }

    private onImageFieldChange(e: Event)
    {
        if (this.imageField.files.length == 1)
        {
            this.helpers.setButtonEnabled(this.addImageButton, false);
            this.imageFilenameField.value = this.imageField.files[0].name;

            new Promise((resolve, reject) =>
            {
                let reader = new FileReader();
                reader.addEventListener('load', (e) =>
                {
                    this.data.imageData = reader.result as string;
                    
                    this.helpers.setButtonEnabled(this.addImageButton, true);

                    this.clearImageButton.disabled = false;
                    this.saveImageButton.disabled = false;
                    this.imagePreview.src = reader.result as string;
                    this.imagePreview.style.display = 'block';
                    resolve();
                });
        
                reader.readAsDataURL(this.imageField.files[0]);
            });
        }
    }

    private onClearImageButtonClick(e: MouseEvent, sender: HTMLButtonElement)
    {
        this.imageFilenameField.value = null;
        this.imageField.value = null;
        this.clearImageButton.disabled = true;
        this.imagePreview.src = null;
        this.imagePreview.style.display = 'none';
        this.saveImageButton.disabled = true;
    }

    private onSaveImageButtonClick(e: MouseEvent, sender: HTMLButtonElement)
    {
        if (this.data.imageData == null)
        {
            this.app.modals.show(new MessageModal({
                color: ModalColor.RED,
                message: this.pgettext(
                    'decontamination.Order', 
                    'Please select an image before continuing.'
                )
            }));
            return;
        }

        const done = () => {
            this.helpers.setButtonEnabled(sender, true);
        };

        this.helpers.setButtonEnabled(sender, false);
        new ConfirmModal({
            message: this.pgettext(
                'decontamination.message',
                'Are you sure you want to upload this image?'
            )
        }).prompt().then(confirmed => {
            if (!confirmed) {
                done();
                return;
            }
            
            const reader = new FileReader();
            return new Promise((resolve, reject) => {
                reader.onload = () => {
                    const image = new Image();
                    image.onload = () => {
                        resolve({url: reader.result, width: image.width, height: image.height});
                    };
                    image.src = reader.result as string;
                };
                reader.onerror = reject;
                reader.readAsDataURL(this.imageField.files[0]);
            }).then((image: {url: string, width: number, height: number}) => {
                return this.app.db.DeconOrderImage.add({
                    order_id: this.data.order.id,
                    file: image.url,
                    width: image.width,
                    height: image.height,
                    is_offline: 1,
                });
            }).then(() => {
                return this.app.synchronizer.createMessageAndPull({
                    action: ClientAction.DECONTAMINATION.ADD_ORDER_IMAGE,
                    data: {
                        order_id: this.data.order.id,
                        filename: this.imageField.files[0].name,
                        image_base64_data: this.data.imageData
                    }
                }, false).then(() =>
                {
                    this.app.refreshCurrentView({tab: 'photographing'}).then(() =>
                    {
                        this.app.toasts.show(this.app.pgettext(
                            'decontamination.message',
                            'Successfully added an image!'
                        ));
                    })
                }, () =>
                {
                    done();
                });
            }); 
        });
    }

    private onRemoveImageButtonClick(e: MouseEvent, sender: HTMLButtonElement)
    {
        this.helpers.setButtonEnabled(sender, false);
        const done = () => {
            this.helpers.setButtonEnabled(sender, true);
        };

        new ConfirmModal({
            message: this.pgettext(
                'decontamination.message',
                'Are you sure you want to remove this image?'
            )
        }).prompt().then((confirmed) => {
            if (!confirmed) {
                done();
                return;
            }
            return this.app.db.DeconOrderImage.where('id').equals(parseInt(sender.getAttribute('data-image-id'))).delete().then(() => {
                return this.app.synchronizer.createMessageAndPull({
                    action: ClientAction.DECONTAMINATION.REMOVE_ORDER_IMAGE,
                    data: {
                        order_id: this.data.order.id,
                        image_id: sender.getAttribute('data-image-id')
                    }
                }, false).then(() => {
                    this.app.refreshCurrentView({tab: 'photographing'}).then(() =>
                    {
                        this.app.toasts.show(this.app.pgettext(
                            'decontamination.message',
                            'Successfully removed an image!'
                        ));
                    })
                }, () => {
                    done();
                });
            });
        });
    }
}

export interface OrderPhotographingTabViewData extends OrderDetailViewData
{
    imageData?: string;
}